import { useCallback, useState } from "react";
import "./CustomFooter.scss";
import { Box, Typography, Grid } from "@mui/material";
import {
  contactUsEmail,
  contactUsTimings,
  GTAScreenType,
  privacyPolicyTitle,
  SCREEN_TYPE,
  termsAndConditionsTitle,
  tollFreeNumber,
  tollFreeTitle,
} from "../../utils/constants";
import { triggerCleverTapEvent } from "../../utils/CleverTap";
import TriggerGTAEvent from "../../utils/GTA";
import abcLogo from "../../assets/images/login-revamp-footer-abc-logo.svg";
import { Colors } from "../../styles/constant";
import tollFreeIcon from "../../assets/icons/login-revamp-footer-tollFree-icon.svg";
import playStoreImg from "../../assets/images/login-revamp-footer-abc-playStore.svg";
import appStoreImg from "../../assets/images/login-revamp-footer-abc-appStore.svg";
import qrCodeImg from "../../assets/images/login-revamp-footer-abc-qr-code.svg";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import useCmsPageVariables from "../../hooks/useCmsPageVariables";
import { windowOpen } from "../../utils/helper";
import useWindowSize from "../../hooks/useWindowSize";
import { isEmpty } from "lodash";

export const Footer = () => {
  const { width } = useWindowSize();
  const [activeSection, setActiveSection] = useState<
    "contact" | "useful" | null
  >("contact");
  const termsAndConditionsCmsData = useCmsPageVariables(
    termsAndConditionsTitle,
  );
  const privacyPolicyCmsData = useCmsPageVariables(privacyPolicyTitle);

  const tollFreeSectionData = useCmsPageVariables("toll-free-number-section");
  const contactUsSectionData = useCmsPageVariables("contact-us-footer-section");
  const downloadTheAppSectionData = useCmsPageVariables(
    "download-the-app-footer-section",
  );

  const openUrl = useCallback((url: string) => {
    if (isEmpty(url)) return;
    windowOpen(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleFooterLinkClick = useCallback(
    (label: "Terms & Conditions" | "Privacy Policy", url: string) => {
      triggerCleverTapEvent("serv_footer_interaction", {
        ScreenType: SCREEN_TYPE.generalAppType,
        ScreenName: "Servicing Login Mobile Number Enter Details Screen",
        isPrevPageNameRequired: true,
        ctaText: label,
      });

      TriggerGTAEvent("serv_footer_interaction", {
        screen_type: GTAScreenType.LoginScreenType,
        screen_name: "serv_login_mobile_number_enter_details_screen",
        CTA_text: label,
      });
      triggerGTA(label);
      openUrl(url);
    },
    [openUrl],
  );

  const triggerGTA = (ctaText: string) => {
    TriggerGTAEvent("serv_bottom_cta_click", {
      screen_type: GTAScreenType.LoginScreenType,
      screen_name: "serv_login_mobile_number_enter_details_screen",
      CTA_text: ctaText,
    });
  };

  // Usage:
  const handleTermsAndConditionsClick = () =>
    handleFooterLinkClick(
      "Terms & Conditions",
      termsAndConditionsCmsData?.attributes?.url || "",
    );

  const handlePrivacyPolicyClick = () =>
    handleFooterLinkClick(
      "Privacy Policy",
      privacyPolicyCmsData?.attributes?.url || "",
    );

  const handleToggle = (section: "contact" | "useful") => {
    // Close if clicking the same active section
    let tempSection = section === "contact" ? "contact us" : "useful links";
    triggerGTA(tempSection);
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const renderDownloadAppNowSection = () => {
    return (
      <Box display="flex" gap="1rem" flexDirection="column" alignItems="center">
        <Typography
          variant="h5"
          letterSpacing=".0938rem"
          textTransform="uppercase"
          color="#FFFFFF"
        >
          Download the app now
        </Typography>
        <Box display="flex" gap="2.5rem" flexDirection="row">
          <img
            src={
              downloadTheAppSectionData?.attributes?.infographic?.data
                ?.attributes?.url || qrCodeImg
            }
            style={{ cursor: "pointer" }}
          ></img>
          <Box
            width="0rem"
            height="5.75rem"
            border=".0313rem solid #FFFFFF"
            sx={{ opacity: "60%" }}
          ></Box>
          <Box display="flex" flexDirection="column" gap=".75rem">
            {[playStoreImg, appStoreImg].map((imgSrc, index) => (
              <img
                key={index}
                src={imgSrc}
                onClick={() => {
                  triggerGTA("download app");
                  openUrl(downloadTheAppSectionData?.attributes?.url || "");
                }}
                style={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  // Reusable toggle header
  const ToggleHeader = ({
    label,
    section,
    activeSection,
    onToggle,
  }: {
    label: string;
    section: "contact" | "useful";
    activeSection: "contact" | "useful" | null;
    onToggle: (section: "contact" | "useful") => void;
  }) => {
    const isActive = activeSection === section;
    const color = isActive ? Colors.darkCharcoal : Colors.descriptionGray;

    return (
      <Box
        display="flex"
        padding=".5rem .75rem"
        gap=".625rem"
        alignItems="center"
        onClick={() => onToggle(section)}
        sx={{ cursor: "pointer" }}
      >
        {isActive ? (
          <RemoveIcon fontSize="medium" sx={{ color }} />
        ) : (
          <AddIcon fontSize="medium" sx={{ color }} />
        )}
        <Typography variant="h5" color={color}>
          {label}
        </Typography>
      </Box>
    );
  };

  const renderLogoAndContactLinks = (isWeb?: boolean) => {
    return (
      <Box
        display="flex"
        gap={isWeb ? "4rem" : "2rem"}
        flexDirection={isWeb ? "row" : "column"}
        alignItems={isWeb ? "center" : "unset"}
      >
        <Box
          display="flex"
          gap=".5rem"
          flexDirection={isWeb ? "column" : "row"}
        >
          <img
            src={abcLogo}
            style={{
              width: isWeb ? "10.1231rem" : "",
              height: isWeb ? "5rem" : "",
            }}
          />
          <Box
            display="flex"
            flexDirection="row"
            padding=".25rem .5rem .25rem .5rem"
            gap=".5rem"
            border=".0625rem solid #FFFFFF"
            bgcolor={Colors.bgGray}
            flex={isWeb ? "" : "1"}
          >
            <img
              src={
                (isWeb
                  ? tollFreeSectionData?.attributes?.image?.data?.attributes
                      ?.url
                  : tollFreeSectionData?.attributes?.mobileImage?.data
                      ?.attributes?.url) || tollFreeIcon
              }
            />
            <Box
              display="flex"
              flexDirection="column"
              gap=".25rem"
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="h6" color="#77787B">
                {tollFreeSectionData?.attributes?.title || tollFreeTitle}
              </Typography>
              <Typography variant="h5" fontWeight={500} color={Colors.red}>
                {tollFreeSectionData?.attributes?.text || tollFreeNumber}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: isWeb ? "33.0056rem" : undefined }}>
          <Box display="flex" flexDirection="row">
            <ToggleHeader
              label="CONTACT US"
              section="contact"
              activeSection={activeSection}
              onToggle={handleToggle}
            />
            <ToggleHeader
              label="USEFUL LINKS"
              section="useful"
              activeSection={activeSection}
              onToggle={handleToggle}
            />
          </Box>
          {/* White content box - only shown if a section is active */}
          {activeSection && (
            <Box
              display="flex"
              flexDirection="column"
              gap=".625rem"
              padding=".75rem"
              sx={{
                borderBottomLeftRadius: ".75rem",
                borderBottomRightRadius: ".75rem",
                backgroundColor: "white",
              }}
            >
              {activeSection === "contact" && (
                <Typography
                  variant="h5"
                  fontWeight={400}
                  color={Colors.darkCharcoal}
                >
                  Toll Free Number -{" "}
                  {contactUsSectionData?.attributes?.title || tollFreeNumber} |{" "}
                  Email -{" "}
                  {contactUsSectionData?.attributes?.text || contactUsEmail}
                  <br />
                  {contactUsSectionData?.attributes?.urlLable ||
                    contactUsTimings}
                </Typography>
              )}

              {activeSection === "useful" && (
                <Box display="flex" flexDirection="row">
                  <Typography
                    variant="h5"
                    color={Colors.darkCharcoal}
                    marginRight=".25rem"
                    onClick={handleTermsAndConditionsClick}
                    sx={{ cursor: "pointer" }}
                  >
                    Terms and conditions |
                  </Typography>
                  <Typography
                    variant="h5"
                    color={Colors.darkCharcoal}
                    onClick={handlePrivacyPolicyClick}
                    sx={{ cursor: "pointer" }}
                  >
                    Privacy policy
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderFooter = (isWeb: boolean) => (
    <Box className={isWeb ? "footer-outer-box-web" : "footer-outer-box-mobile"}>
      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          {renderLogoAndContactLinks(isWeb)}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderDownloadAppNowSection()}
        </Grid>
      </Grid>
    </Box>
  );

  return width < 900 ? renderFooter(false) : renderFooter(true);
};
